/**
 * Use Case Templates - Client Type Definitions (DC-1077)
 *
 * Client-side type definitions for use case templates and assessments.
 * Mirrors server types for type safety.
 */

// ============================================================================
// ENUMS & CONSTANTS
// ============================================================================

export type TemplateDomain = 'pharma' | 'biotech' | 'medtech' | 'generic';
export type TemplateCategory = 'regulatory' | 'research' | 'commercial' | 'operations';
export type RiskLevel = 'low' | 'medium' | 'high' | 'critical';
export type AssessmentStatus = 'pending' | 'running' | 'completed' | 'failed';
export type IntakeQuestionType = 'text' | 'select' | 'multiselect' | 'boolean' | 'range';

export type StationId =
  | 'data_quality'
  | 'live_checks'
  | 'interviews'
  | 'documents'
  | 'ml_readiness'
  | 'controlled_vocabularies'
  | 'ontology_inference';

// ============================================================================
// INTAKE QUESTIONS
// ============================================================================

export interface IntakeQuestionOption {
  value: string;
  label: string;
}

export interface IntakeQuestionValidation {
  min?: number;
  max?: number;
  pattern?: string;
  message?: string;
}

export interface IntakeQuestion {
  id: string;
  question: string;
  type: IntakeQuestionType;
  options?: IntakeQuestionOption[];
  required: boolean;
  default_value?: string | number | boolean | string[];
  validation?: IntakeQuestionValidation;
  help_text?: string;
}

// ============================================================================
// TEMPLATES
// ============================================================================

export interface UseCaseTemplateSummary {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  domain: TemplateDomain;
  category: TemplateCategory;
  risk_level: RiskLevel;
  is_system_template: boolean;
  required_stations: StationId[];
  version: number;
}

export interface UseCaseTemplate extends UseCaseTemplateSummary {
  organization_id: string | null;
  optional_stations: StationId[];
  station_weights: Partial<Record<StationId, number>>;
  station_configs: Record<string, unknown>;
  custom_validations: CustomValidation[];
  required_ontologies: string[];
  minimum_thresholds: Partial<Record<StationId, number>>;
  intake_questions: IntakeQuestion[];
  is_active: boolean;
  created_at: string;
  updated_at: string;
  created_by: string | null;
}

export interface CustomValidation {
  type: string;
  minimum_score?: number;
  required_findings?: string[];
  error_message?: string;
}

// ============================================================================
// ASSESSMENTS
// ============================================================================

export interface UseCaseAssessment {
  id: string;
  journey_id: string;
  template_id: string;
  organization_id: string;
  template_snapshot: UseCaseTemplate;
  intake_answers: Record<string, string | number | boolean | string[]>;
  weighted_score: number | null;
  station_scores: Partial<Record<StationId, number>> | null;
  validation_results: Record<string, boolean> | null;
  threshold_compliance: Partial<Record<StationId, boolean>> | null;
  status: AssessmentStatus;
  started_at: string | null;
  completed_at: string | null;
  error_message: string | null;
  created_at: string;
  updated_at: string;
}

// ============================================================================
// API TYPES
// ============================================================================

export interface TemplateListResponse {
  success: boolean;
  templates: UseCaseTemplateSummary[];
  count: number;
}

export interface TemplateDetailResponse {
  success: boolean;
  template: UseCaseTemplate;
}

export interface AssessmentResponse {
  success: boolean;
  assessment: UseCaseAssessment;
}

export interface IntakeValidationResponse {
  success: boolean;
  valid: boolean;
  errors?: Record<string, string>;
}

// ============================================================================
// UI HELPER TYPES
// ============================================================================

export interface TemplateListFilters {
  domain?: TemplateDomain;
  category?: TemplateCategory;
  risk_level?: RiskLevel;
  include_system?: boolean;
}

export type IntakeAnswerValue = string | number | boolean | string[];

// ============================================================================
// CONSTANTS
// ============================================================================

export const DOMAIN_LABELS: Record<TemplateDomain, string> = {
  pharma: 'Pharmaceutical',
  biotech: 'Biotechnology',
  medtech: 'Medical Technology',
  generic: 'Generic',
};

export const CATEGORY_LABELS: Record<TemplateCategory, string> = {
  regulatory: 'Regulatory',
  research: 'Research',
  commercial: 'Commercial',
  operations: 'Operations',
};

export const RISK_LEVEL_CONFIG: Record<RiskLevel, { label: string; color: string; bgColor: string }> = {
  low: { label: 'Low Risk', color: '#059669', bgColor: '#D1FAE5' },
  medium: { label: 'Medium Risk', color: '#D97706', bgColor: '#FEF3C7' },
  high: { label: 'High Risk', color: '#DC2626', bgColor: '#FEE2E2' },
  critical: { label: 'Critical Risk', color: '#7C3AED', bgColor: '#EDE9FE' },
};

export const STATION_LABELS: Record<StationId, string> = {
  data_quality: 'Data Quality',
  live_checks: 'Live Checks',
  interviews: 'Interviews',
  documents: 'Documents',
  ml_readiness: 'ML Readiness',
  controlled_vocabularies: 'Controlled Vocabularies',
  ontology_inference: 'Ontology Inference',
};

export const CATEGORY_ICONS: Record<TemplateCategory, string> = {
  regulatory: '📋',
  research: '🔬',
  commercial: '📈',
  operations: '⚙️',
};
